import { Paperclip, X } from 'lucide-react'
import { useRef } from 'react'
import { Button } from '@/components/ui/button'

interface Props {
  /** Files as picked. They are uploaded by the send mutation, not by this field. */
  value: File[]
  onChange: (files: File[]) => void
  label?: string
  disabled?: boolean
  'data-testid'?: string
}

/**
 * Screenshot / file picker for a feedback submission or thread reply.
 *
 * Picking a file does not upload it — the send mutation does, the same way a
 * message attachment works. Nothing is ever in flight while the form sits open,
 * so an abandoned form leaves no orphaned object in storage and Send can never
 * outrun an upload.
 */
export function FeedbackAttachmentField({
  value,
  onChange,
  label = 'Screenshot',
  disabled,
  'data-testid': testId = 'feedback-attachment-input',
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null)

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        data-testid={testId}
        onChange={(e) => {
          if (e.target.files?.length) onChange([...value, ...Array.from(e.target.files)])
          e.target.value = ''
        }}
      />

      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
      >
        <Paperclip className="h-4 w-4" />
        {label}
      </Button>

      {value.length > 0 && (
        <ul className="space-y-1">
          {value.map((file, index) => (
            <li
              key={`${file.name}-${index}`}
              className="flex items-center gap-2 text-xs text-muted-foreground"
            >
              <Paperclip className="h-3 w-3 shrink-0" />
              <span className="truncate">{file.name}</span>
              <button
                type="button"
                className="ml-auto rounded p-0.5 hover:bg-muted"
                aria-label={`Remove ${file.name}`}
                onClick={() => onChange(value.filter((_, i) => i !== index))}
              >
                <X className="h-3 w-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
